/**
 * aggregateAwareness — classify SELECT-list items as aggregate / non-aggregate
 * and surface a "auto-add to GROUP BY?" hint when a query mixes the two.
 *
 * Detection is text-based: we scan each SelectItem's expressionText for a
 * top-level aggregate function call (SUM/COUNT/AVG/MIN/MAX + the SQL-standard
 * helpers). Items with a top-level OVER (...) — i.e. window functions —
 * intentionally do NOT count as aggregates: a `SUM(x) OVER (...)` does not
 * collapse rows and never forces a GROUP BY clause.
 *
 * The column-text returned for a flagged item is the full expressionText
 * trimmed; downstream callers feed it directly to `addGroupBy` (SR-Edit's
 * `add-group-by` op accepts opaque expression text).
 *
 * PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/remove GROUP BY.
 */

import type { QueryStructure, SelectItem } from '../../../sql-resolver/types/queryStructure';

/**
 * Canonical aggregate function names. Engine-extensible (we list the
 * dialect-shared core; engine-specific aggregates like `STRING_AGG` are
 * covered too because they're widely supported). Stored uppercase.
 */
export const AGGREGATE_FUNCTION_NAMES: ReadonlySet<string> = new Set<string>([
  'COUNT',
  'SUM',
  'AVG',
  'MIN',
  'MAX',
  'ARRAY_AGG',
  'STRING_AGG',
  'GROUP_CONCAT',
  'LISTAGG',
  'BOOL_AND',
  'BOOL_OR',
  'EVERY',
  'JSON_AGG',
  'JSONB_AGG',
  'JSON_OBJECT_AGG',
  'JSONB_OBJECT_AGG',
  'STDDEV',
  'STDDEV_POP',
  'STDDEV_SAMP',
  'VARIANCE',
  'VAR_POP',
  'VAR_SAMP',
  'MEDIAN',
  'PERCENTILE_CONT',
  'PERCENTILE_DISC',
  'GROUPING',
]);

export type SelectItemAggregateClass = 'aggregate' | 'non-aggregate';

export interface SelectItemClassification {
  readonly index: number;
  readonly classification: SelectItemAggregateClass;
  readonly columnText: string;
  /**
   * True when this non-aggregate item would need to appear in GROUP BY for
   * the SELECT to be valid (the query mixes aggregates with non-aggregates,
   * AND this item's column text is not already present in GROUP BY).
   */
  readonly needsGroupBy: boolean;
}

/**
 * Classify a SELECT-list item.
 * - Window-function items (`item.window` set) are always non-aggregate from
 *   the GROUP BY perspective: window functions do not collapse rows.
 * - Otherwise we scan `expressionText` for a known aggregate function call.
 */
export function classifySelectItem(item: SelectItem): SelectItemAggregateClass {
  if (item.window) return 'non-aggregate';
  const text = item.expressionText ?? '';
  return containsAggregateCall(text) ? 'aggregate' : 'non-aggregate';
}

/**
 * Detects whether `expression` contains a function call whose head is one of
 * the known aggregate names. Lightweight scan — strips comments + string
 * literals before matching to avoid false positives like `'SUM(x)'`.
 */
export function containsAggregateCall(expression: string): boolean {
  const stripped = stripSubqueries(stripStringsAndComments(expression));
  // Match an identifier immediately followed by `(`, capturing the name.
  const re = /([A-Za-z_][A-Za-z0-9_]*)\s*\(/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(stripped)) !== null) {
    const name = m[1].toUpperCase();
    if (AGGREGATE_FUNCTION_NAMES.has(name)) return true;
  }
  return false;
}

/**
 * Build a per-item classification list for the SELECT clause, computing the
 * `needsGroupBy` flag using the structure's `groupBy` clause for comparison.
 *
 * The query "mixes" when at least one item is aggregate AND at least one is
 * non-aggregate. Without a mix, no warnings — pure aggregate (`COUNT(*)`)
 * and pure non-aggregate queries are both valid without GROUP BY.
 */
export function classifySelectItems(
  structure: QueryStructure | null | undefined,
): readonly SelectItemClassification[] {
  const items = structure?.selectItems ?? [];
  const groupByTexts = (structure?.groupBy ?? []).map((g) => g.qualified.raw);

  const classifications = items.map((item, index): SelectItemClassification => {
    const cls = classifySelectItem(item);
    const columnText = (item.expressionText ?? '').trim();
    return {
      index,
      classification: cls,
      columnText,
      needsGroupBy: false, // filled after we know whether the query mixes
    };
  });

  const hasAggregate = classifications.some((c) => c.classification === 'aggregate');
  const hasNonAggregate = classifications.some((c) => c.classification === 'non-aggregate');
  if (!hasAggregate || !hasNonAggregate) return classifications;

  return classifications.map((c) => {
    if (c.classification !== 'non-aggregate') return c;
    if (c.columnText.trim().length === 0) return c;
    if (groupByTexts.some((g) => columnTextsMatch(c.columnText, g))) return c;
    return { ...c, needsGroupBy: true };
  });
}

function normalizeColumnText(text: string): string {
  return text.trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Parse a "simple column reference" — an optionally-qualified identifier like
 * `country`, `c.country`, `"c"."country"`, `` `c`.`country` ``, `[c].[country]`.
 * Returns null for anything more complex (expressions, function calls).
 */
function parseSimpleColumnRef(
  text: string,
): { readonly qualifier: string | null; readonly column: string } | null {
  const IDENT = '(?:[A-Za-z_][\\w$]*|"[^"]+"|`[^`]+`|\\[[^\\]]+\\])';
  const re = new RegExp(`^(?:(${IDENT})\\s*\\.\\s*)?(${IDENT})$`);
  const m = re.exec(text.trim());
  if (!m) return null;
  return {
    qualifier: m[1] ? unquoteIdentifier(m[1]) : null,
    column: unquoteIdentifier(m[2]),
  };
}

function unquoteIdentifier(raw: string): string {
  const t = raw.trim();
  if (
    (t.startsWith('"') && t.endsWith('"')) ||
    (t.startsWith('`') && t.endsWith('`'))
  ) {
    return t.slice(1, -1).toLowerCase();
  }
  if (t.startsWith('[') && t.endsWith(']')) return t.slice(1, -1).toLowerCase();
  return t.toLowerCase();
}

/**
 * True when a SELECT-item text and a GROUP BY-entry text refer to the same
 * column. Exact normalized-text match first; then, when BOTH sides are simple
 * column references, match on the column name — with the qualifier compared
 * only when both sides carry one (a bare side matches any qualifier).
 */
function columnTextsMatch(selectText: string, groupByText: string): boolean {
  if (normalizeColumnText(selectText) === normalizeColumnText(groupByText)) return true;
  const a = parseSimpleColumnRef(selectText);
  const b = parseSimpleColumnRef(groupByText);
  if (!a || !b) return false;
  if (a.column !== b.column) return false;
  if (a.qualifier === null || b.qualifier === null) return true;
  return a.qualifier === b.qualifier;
}

/**
 * Blanks out parenthesized subqueries — any `(` whose first token is SELECT,
 * through its matching `)` — so the aggregate scan only sees the CURRENT
 * query level. A `SUM( (SELECT …) )` stays detectable: only the inner
 * parenthesized SELECT block is removed, the SUM( head survives.
 */
function stripSubqueries(input: string): string {
  let out = '';
  let i = 0;
  const n = input.length;
  while (i < n) {
    if (input[i] === '(') {
      let j = i + 1;
      while (j < n && /\s/.test(input[j])) j++;
      if (/^select\b/i.test(input.slice(j, j + 7))) {
        let depth = 1;
        let k = i + 1;
        while (k < n && depth > 0) {
          if (input[k] === '(') depth++;
          else if (input[k] === ')') depth--;
          k++;
        }
        i = k;
        continue;
      }
    }
    out += input[i];
    i++;
  }
  return out;
}

/**
 * Removes single- and double-quoted string literals plus line / block comments
 * so the function-call regex can't match inside them. Keeps whitespace shape
 * stable enough that surrounding parens and identifiers remain in place.
 */
function stripStringsAndComments(input: string): string {
  let out = '';
  let i = 0;
  const n = input.length;
  while (i < n) {
    const c = input[i];
    const next = input[i + 1];
    if (c === '-' && next === '-') {
      while (i < n && input[i] !== '\n') i++;
      continue;
    }
    if (c === '/' && next === '*') {
      i += 2;
      while (i < n && !(input[i] === '*' && input[i + 1] === '/')) i++;
      i = Math.min(n, i + 2);
      continue;
    }
    if (c === "'" || c === '"') {
      const quote = c;
      i++;
      while (i < n) {
        if (input[i] === quote && input[i + 1] === quote) {
          i += 2;
          continue;
        }
        if (input[i] === quote) {
          i++;
          break;
        }
        i++;
      }
      continue;
    }
    out += c;
    i++;
  }
  return out;
}
